import { useState } from 'react';
import { Box, Button, Flex, Text, NumberKeypad, Rail } from '../ui';
import { getScoreLimitPresets } from '../../helpers/utils/recentScoreLimits';
import ScoreSetting from '../game/ScoreSetting';
import ScoreSettingsModal from '../modals/ScoreSettingsModal';

interface GameSetupRailProps {
  /** The typed limit, '' for none. */
  value: string;
  onDigit: (digit: string) => void;
  onBackspace: () => void;
  /** ∞ or No limit: clear the limit. */
  onNoLimit: () => void;
  /** A preset chip: replaces the typed limit. */
  onPreset: (limit: number) => void;
}

/**
 * W4 Emerald/Game setup rail (1976:21106), desktop Home only (C8). The score
 * limit (Score limit body: PLAY TO + keypad; keyboard digits work too) and
 * the failed-nil rule (Settings body, UI mode hidden). Both are optional and
 * apply on Start, so the post-Start score-limit dialog is skipped. Starts
 * empty: no limit unless one is typed (decided in chat 14). The No limit /
 * recent presets row matches the new-game card; the keypad is Custom.
 */
function GameSetupRail({
  value,
  onDigit,
  onBackspace,
  onNoLimit,
  onPreset,
}: GameSetupRailProps) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [presets] = useState(getScoreLimitPresets);
  const parsed = value === '' ? null : parseInt(value, 10);
  const isInvalid = parsed !== null && parsed <= 0;
  const caption = isInvalid
    ? 'Must be higher than 0, or tap ∞ to remove the limit.'
    : parsed !== null
      ? `First team to reach ${parsed} wins.`
      : 'The first team to reach this score wins.';

  return (
    <>
      <Rail
        label="Game setup"
        title="GAME SETUP"
        meta="OPTIONAL · ALSO IN SETTINGS"
        scrollCues
        bodyPadding="4px 30px 20px"
      >
        <Flex direction="column" gap="var(--spades-spacing-ml)">
          {/* Emerald/Score limit body (1496:19070). */}
          <Flex direction="column" align="center" gap="calc(var(--spades-spacing-ms) + 2px)">
            <Flex direction="column" align="center" w="100%" lineHeight={1.25}>
              <Text className="limit-eyebrow">PLAY TO</Text>
              <Flex
                data-testid="setup-limit-value"
                minH="62px"
                align="center"
                justify="center"
                fontFamily="var(--spades-font-display)"
                fontSize="var(--spades-font-size-3xl)"
                lineHeight={1.1}
                bgGradient="to-r"
                gradientFrom="var(--spades-accent-numeral-light)"
                gradientTo="var(--spades-accent-numeral-dark)"
                bgClip="text"
                color="transparent"
              >
                {value}
              </Flex>
              <Text
                role={isInvalid ? 'alert' : undefined}
                fontSize="var(--spades-font-size-xs)"
                textAlign="center"
                color={isInvalid ? 'var(--spades-error)' : 'spades.muted'}
              >
                {caption}
              </Text>
            </Flex>
            <Flex
              role="group"
              aria-label="Score limit"
              className="setup-limit-presets"
              data-limit-widths="equal"
              w="100%"
              gap="var(--spades-spacing-sm)"
            >
              {[null, ...presets].map((preset) => (
                <Button
                  key={String(preset)}
                  variant="secondary"
                  className="new-game-limit-option"
                  aria-pressed={
                    preset === null ? value === '' : value === String(preset)
                  }
                  onClick={() =>
                    preset === null ? onNoLimit() : onPreset(preset)
                  }
                >
                  {preset === null ? 'No limit' : preset}
                </Button>
              ))}
            </Flex>
            <Box w="100%">
              <NumberKeypad
                onDigit={onDigit}
                onBackspace={onBackspace}
                onInfinity={onNoLimit}
              />
            </Box>
          </Flex>
          <Box h="1px" bg="var(--spades-border)" />
          {/* Emerald/Settings body, UI mode hidden (1497:19228). */}
          <ScoreSetting
            analyticsContext="setup"
            onOpenScoreHelp={() => setIsHelpOpen(true)}
          />
        </Flex>
      </Rail>
      <ScoreSettingsModal isOpen={isHelpOpen} setIsModalOpen={setIsHelpOpen} />
    </>
  );
}

export default GameSetupRail;
